import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'

export type DialogSize = 'md' | 'lg'

interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  // lg — под ввод текста: markdown-описание в 32rem превращается в колодец.
  size?: DialogSize
  children?: ReactNode
  footer?: ReactNode
}

const widthBySize: Record<DialogSize, string> = {
  md: 'sm:w-[min(92vw,32rem)]',
  lg: 'sm:w-[min(92vw,46rem)]',
}

// Нативный <dialog>: focus-trap и закрытие по Esc — из коробки, без ручной ловушки.
export function Dialog({ open, onClose, title, size = 'md', children, footer }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Опциональный вызов: среды без нативного <dialog> (тест-DOM) не роняют компонент.
    if (open && !el.open) el.showModal?.()
    else if (!open && el.open) el.close?.()
  }, [open])

  // Клик по backdrop закрывает. target — сам <dialog>, но клик по его паддингу
  // тоже: отличаем по попаданию координат в бокс окна. Клавиатурный эквивалент
  // (Esc) даёт нативный <dialog>, поэтому слушатель — не на JSX (a11y-правила),
  // а на элементе.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const outside = (e: MouseEvent) => {
      if (e.target !== el) return false
      const r = el.getBoundingClientRect()
      return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom
    }
    // Закрываем, только если и нажатие, и отпускание пришлись на фон: иначе
    // выделение текста мышью с уходом курсора за край окна убивает форму.
    let pressedOutside = false
    const onPointerDown = (e: PointerEvent) => {
      pressedOutside = outside(e)
    }
    const onClick = (e: MouseEvent) => {
      if (pressedOutside && outside(e)) onClose()
    }
    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('click', onClick)
    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('click', onClick)
    }
  }, [onClose])

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className={cn(
        // Мобила: bottom-sheet во всю ширину (снизу, скруглён только верх),
        // с sm — центрированное модальное окно. max-h + overflow — длинный
        // контент скроллится внутри, не раздувая вьюпорт.
        'mx-0 mt-auto mb-0 max-h-[85dvh] w-full max-w-none overflow-y-auto rounded-t-card rounded-b-none border border-border-default bg-bg-elevated p-4 text-text-primary',
        'sm:m-auto sm:max-h-[90dvh] sm:rounded-card sm:p-6',
        widthBySize[size],
        'backdrop:bg-black/70',
      )}
    >
      {/* Шапка липнет к верху: в длинной форме (дело, выход) заголовок уезжает
          вместе с прокруткой, и на телефоне непонятно, что вообще заполняешь. */}
      <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-4 flex items-start justify-between gap-4 bg-bg-elevated px-4 pt-4 pb-3 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6">
        <h2 className="t-h3">{title}</h2>
        <button
          type="button"
          aria-label="Закрыть"
          onClick={onClose}
          className="text-text-tertiary transition-colors hover:text-text-primary"
        >
          <Icon name="close" />
        </button>
      </div>
      {children && <div className="text-text-secondary">{children}</div>}
      {/* Кнопки не уезжают за край длинной формы: держатся у нижней кромки
          окна. Нижний отступ учитывает home-indicator — диалог прижат к кромке
          экрана (bottom-sheet), и без него «Сохранить» попадает под жест. */}
      {footer && (
        <div className="sticky bottom-0 -mx-4 -mb-4 mt-6 flex justify-end gap-3 border-t border-border-subtle bg-bg-elevated px-4 pt-4 pb-safe-4 sm:-mx-6 sm:-mb-6 sm:px-6 sm:pb-4">
          {footer}
        </div>
      )}
    </dialog>
  )
}
